import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { useQuerySessionById } from '@heidi/session/data';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { getActualSessionPageId } from './actual-session-page';
import {
  getEhrLinkDebugDetails,
  type EhrLinkDebugDetails,
} from './ehr-link-debug-details';
import { EhrLinkDetailsResult } from './ehr-link-details-result';

const messages = defineMessages({
  heading: {
    defaultMessage: 'EHR link details',
    id: 'components.dev.integration.ehrLinks.heading',
  },
  description: {
    defaultMessage:
      'Fetches session and consult-note links, then shows them beside patient-profile identifiers and legacy ehr_* fields stored on the session.',
    id: 'components.dev.integration.ehrLinks.description',
  },
  sessionOnly: {
    defaultMessage:
      'This tool only works on an actual session page. Open /scribe/session/{id} or /scribe/sessions/{id}.',
    id: 'components.dev.integration.ehrLinks.sessionOnly',
  },
  sessionLoading: {
    defaultMessage: 'The current session is still loading.',
    id: 'components.dev.integration.ehrLinks.sessionLoading',
  },
  currentSession: {
    defaultMessage: 'Current session: {sessionId}',
    id: 'components.dev.integration.ehrLinks.currentSession',
  },
  getDetails: {
    defaultMessage: 'Get EHR link details',
    id: 'components.dev.integration.ehrLinks.getDetails',
  },
  loadingDetails: {
    defaultMessage: 'Loading EHR link details…',
    id: 'components.dev.integration.ehrLinks.loadingDetails',
  },
  error: {
    defaultMessage: 'Could not fetch EHR link details.',
    id: 'components.dev.integration.ehrLinks.error',
  },
});

interface RequestState {
  data?: EhrLinkDebugDetails;
  error?: string;
  isLoading: boolean;
}

export function EhrLinksDevSection() {
  const intl = useIntl();
  const router = useRouter();
  const sessionId = getActualSessionPageId(router);
  const { data: session, isLoading: isSessionLoading } = useQuerySessionById({
    sessionId: sessionId ?? '',
  });
  const [requestState, setRequestState] = useState<RequestState>({
    isLoading: false,
  });

  const handleGetDetails = async () => {
    if (!session) return;

    setRequestState({ isLoading: true });
    try {
      const data = await getEhrLinkDebugDetails(session);
      setRequestState({ data, isLoading: false });
    } catch (error) {
      setRequestState({
        error:
          error instanceof Error && error.message
            ? error.message
            : intl.formatMessage(messages.error),
        isLoading: false,
      });
    }
  };

  return (
    <section aria-labelledby="dev-tools-integration-ehr-links-heading" className="mt-5">
      <TypographyP2SemiBold id="dev-tools-integration-ehr-links-heading" className="mb-2">
        {intl.formatMessage(messages.heading)}
      </TypographyP2SemiBold>
      <div className="space-y-3">
        <TypographyCaption className="text-text-tertiary">
          {intl.formatMessage(messages.description)}
        </TypographyCaption>

        {!sessionId ? (
          <TypographyCaption className="text-validation-warning">
            {intl.formatMessage(messages.sessionOnly)}
          </TypographyCaption>
        ) : isSessionLoading || !session ? (
          <TypographyCaption className="text-text-tertiary">
            {intl.formatMessage(messages.sessionLoading)}
          </TypographyCaption>
        ) : (
          <TypographyCaption className="text-text-secondary">
            {intl.formatMessage(messages.currentSession, { sessionId })}
          </TypographyCaption>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          disabled={!sessionId || !session || isSessionLoading || requestState.isLoading}
          onClick={() => {
            void handleGetDetails();
          }}
        >
          {intl.formatMessage(messages.getDetails)}
        </Button>

        {requestState.isLoading ? (
          <TypographyCaption className="text-text-tertiary">
            {intl.formatMessage(messages.loadingDetails)}
          </TypographyCaption>
        ) : null}
        {requestState.error ? (
          <TypographyCaption role="alert" className="text-validation-destructive">
            {requestState.error}
          </TypographyCaption>
        ) : null}
        {requestState.data ? <EhrLinkDetailsResult details={requestState.data} /> : null}
      </div>
    </section>
  );
}
